import { useMemo, useState } from 'react';
import { DollarSign, Layers, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useTradeableCatalogQuery } from '@/client/api/promotion-pricing/tradeable-catalog.query';
import { useDeleteTradeableObjectMutation } from '@/client/api/promotion-pricing/tradeable-object.mutation';
import EditPlanPriceModal from '@/client/components/admin/Promotion/EditPlanPriceModal';
import PlanConfigModal from '@/client/components/admin/Promotion/PlanConfigModal';
import { PricingStats } from '@/client/components/admin/Promotion/promotion-pricing/pricing-stats';
import { PricingToolbar } from '@/client/components/admin/Promotion/promotion-pricing/pricing-toolbar';
import { TypeAccordion } from '@/client/components/admin/Promotion/promotion-pricing/type-accordion';
import { getTypeMeta } from '@/client/components/admin/Promotion/promotion-pricing/catalog-meta';
import { DEFAULT_CCODE } from '@/client/types/promotion-pricing';
import { CatalogPlan, planTitle, asNum } from '@/client/types/promotion-pricing/catalog';

const CURRENCIES = [DEFAULT_CCODE];

interface TypeGroup {
  totid: number;
  plans: CatalogPlan[];
}

const PromotionPricingPage = () => {
  useBreadcrumbs([{ label: 'Promotions', href: '#' }, { label: 'Pricing' }]);

  // Currency, search and type filter persist in the URL (?ccode=&q=&totid=).
  const { filters, handleFilterChange, searchTerm, handleSearch } = useListFilters<{
    ccode: string;
    totid: string;
  }>({ initialFilters: { ccode: DEFAULT_CCODE, totid: 'all' } });
  const ccode = filters.ccode;
  const search = searchTerm;
  const totidFilter: number | 'all' =
    filters.totid === 'all' || filters.totid === '' ? 'all' : Number(filters.totid);
  const setCcode = (v: string) => handleFilterChange('ccode', v);
  const setSearch = handleSearch;
  const setTotidFilter = (v: number | 'all') => handleFilterChange('totid', v);
  const [openTypes, setOpenTypes] = useState<Set<number>>(new Set());

  const [selectedPlan, setSelectedPlan] = useState<CatalogPlan | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Create / edit-config modal.
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [configPlan, setConfigPlan] = useState<CatalogPlan | null>(null);
  const [configSeedTotid, setConfigSeedTotid] = useState<number | undefined>(undefined);

  // Delete confirmation.
  const [planToDelete, setPlanToDelete] = useState<CatalogPlan | null>(null);
  const deleteMutation = useDeleteTradeableObjectMutation();

  const { data: plans = [], isLoading } = useTradeableCatalogQuery(ccode);

  const typeOptions = useMemo(
    () =>
      Array.from(new Set(plans.map((p) => p.totid))).sort(
        (a, b) => getTypeMeta(a).order - getTypeMeta(b).order,
      ),
    [plans],
  );

  // Group by type, apply search + type filter, and sort plans within a group.
  const groups = useMemo<TypeGroup[]>(() => {
    const q = search.trim().toLowerCase();
    const byType = new Map<number, CatalogPlan[]>();

    for (const plan of plans) {
      if (totidFilter !== 'all' && plan.totid !== totidFilter) continue;
      if (q && !planTitle(plan).toLowerCase().includes(q) && !plan.name.toLowerCase().includes(q))
        continue;
      const list = byType.get(plan.totid) ?? [];
      list.push(plan);
      byType.set(plan.totid, list);
    }

    return Array.from(byType.entries())
      .sort(([a], [b]) => getTypeMeta(a).order - getTypeMeta(b).order)
      .map(([totid, items]) => ({
        totid,
        plans: items
          .slice()
          .sort(
            (a, b) =>
              a.order_by - b.order_by ||
              (asNum(a.sub_duration) ?? 0) - (asNum(b.sub_duration) ?? 0) ||
              (asNum(a.tier) ?? 0) - (asNum(b.tier) ?? 0),
          ),
      }));
  }, [plans, search, totidFilter]);

  const toggleType = (totid: number) =>
    setOpenTypes((prev) => {
      const next = new Set(prev);
      next.has(totid) ? next.delete(totid) : next.add(totid);
      return next;
    });

  const handleEditPrice = (plan: CatalogPlan) => {
    setSelectedPlan(plan);
    setIsEditOpen(true);
  };

  const handleCreatePlan = (totid?: number) => {
    setConfigPlan(null);
    setConfigSeedTotid(totid);
    setIsConfigOpen(true);
  };

  const handleEditConfig = (plan: CatalogPlan) => {
    setConfigPlan(plan);
    setConfigSeedTotid(undefined);
    setIsConfigOpen(true);
  };

  const closeConfig = () => {
    setIsConfigOpen(false);
    setConfigPlan(null);
    setConfigSeedTotid(undefined);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;
    try {
      await deleteMutation.mutateAsync({ toid: planToDelete.toid });
      toast.success(`Plan "${planToDelete.name}" deleted`, { richColors: true });
      setPlanToDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete plan', { richColors: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        {/* Header */}
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <DollarSign className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Plans &amp; Pricing</h1>
          </div>
          <p className="text-sm text-gray-500">
            Browse the tradeable-object catalog grouped by type. Expand a group to inspect plans,
            durations, features and configuration, and set list prices ({ccode}).
          </p>
        </div>

        {!isLoading && plans.length > 0 && <PricingStats plans={plans} />}

        <PricingToolbar
          search={search}
          onSearch={setSearch}
          ccode={ccode}
          onCcode={setCcode}
          currencies={CURRENCIES}
          totid={totidFilter}
          onTotid={setTotidFilter}
          typeOptions={typeOptions}
          onExpandAll={() => setOpenTypes(new Set(groups.map((g) => g.totid)))}
          onCollapseAll={() => setOpenTypes(new Set())}
          onCreate={() => handleCreatePlan()}
        />

        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[72px] w-full rounded-xl" />
            ))}
          </div>
        )}

        {/* Empty */}
        {!isLoading && groups.length === 0 && (
          <div className="rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <Layers className="h-8 w-8 text-gray-400" />
              </div>
              <p className="font-medium text-gray-500">No plans match your filters</p>
              <p className="text-sm text-gray-400">Try clearing the search or type filter.</p>
            </div>
          </div>
        )}

        {/* Groups */}
        {!isLoading && (
          <div className="space-y-3">
            {groups.map((group) => (
              <TypeAccordion
                key={group.totid}
                totid={group.totid}
                plans={group.plans}
                ccode={ccode}
                open={openTypes.has(group.totid)}
                onToggle={() => toggleType(group.totid)}
                onEditPrice={handleEditPrice}
                onEditConfig={handleEditConfig}
                onDelete={setPlanToDelete}
                onCreate={handleCreatePlan}
              />
            ))}
          </div>
        )}
      </div>

      <EditPlanPriceModal
        isOpen={isEditOpen}
        plan={selectedPlan}
        ccode={ccode}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedPlan(null);
        }}
      />

      <PlanConfigModal
        isOpen={isConfigOpen}
        plan={configPlan}
        seedTotid={configSeedTotid}
        onClose={closeConfig}
      />

      <AlertDialog open={!!planToDelete} onOpenChange={(open) => !open && setPlanToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this plan?</AlertDialogTitle>
            <AlertDialogDescription>
              {planToDelete
                ? `"${planTitle(planToDelete)}" (toid ${planToDelete.toid}) will be deactivated and removed from the catalog. This can be reversed by re-activating it.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete plan'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PromotionPricingPage;
